import {
  addShownPointIds,
  DraftBoundaryPoint,
  NewZoneKind,
  removeShownPointIds,
  setBoundaryEditActivePoint,
  setBoundaryEditPoints,
  startBoundaryEdit,
} from "portal/state/farmExplorer";
import {
  alpha,
  Card,
  CardActionArea,
  IconButton,
  Typography,
} from "@mui/material";
import {
  boundaryEditProblem,
  BoundaryEditProblem,
  ringToDraftPoints,
} from "portal/utils/boundaryEdit";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { moveItem } from "common/utils/arrays";
import { PointList } from "./PointList";
import { roundToNPlaces } from "common/utils/numbers";
import { Section } from "portal/components/map/navigation/Section";
import { SortableContainer } from "portal/components/Sortable";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useCanEditFarmGeometry } from "portal/views/farms/permissions";
import { useDispatch } from "react-redux";
import { useFarmExplorer } from "portal/state/store";
import { useMuiColor } from "common/hooks/useMuiColor";
import { useTranslation } from "react-i18next";
import { Zone as ZoneType } from "protos/farm/farm";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import React, { FC, memo, useCallback, useMemo } from "react";

interface BoundaryRingSectionProps {
  zone: ZoneType;
  areaIndex: number;
  holeIndex?: number;
  header: string;
}

export const BoundaryRingSection: FC<BoundaryRingSectionProps> = ({
  zone,
  areaIndex,
  holeIndex,
  header,
}) => {
  const dispatch = useDispatch();
  const { pointsById, boundaryEdit } = useFarmExplorer();

  const canEditFarm = useCanEditFarmGeometry();

  const polygonProto = zone.areas[areaIndex]?.polygon;
  const ring =
    holeIndex === undefined
      ? polygonProto?.boundary
      : polygonProto?.holes[holeIndex];
  const ringPoints = useMemo(() => ring?.points ?? [], [ring]);
  const ringPointIds = useMemo(
    () => ringPoints.flatMap((p) => p.id?.id ?? []),
    [ringPoints]
  );

  const zoneId = zone.id?.id;
  const isEditingThisRing = Boolean(
    boundaryEdit &&
      zoneId &&
      boundaryEdit.zoneId === zoneId &&
      boundaryEdit.areaIndex === areaIndex &&
      boundaryEdit.holeIndex === holeIndex
  );

  const startEdit = (): void => {
    if (!ring || !zoneId || !pointsById) {
      return;
    }
    dispatch(removeShownPointIds(ringPointIds));
    dispatch(
      startBoundaryEdit({
        zoneId,
        areaIndex,
        holeIndex,
        points: ringToDraftPoints(ring, pointsById),
      })
    );
  };

  return (
    <Section
      // remount so the section is expanded while editing
      key={isEditingThisRing ? "edit" : "view"}
      startCollapsed={!isEditingThisRing}
      onExpand={() => dispatch(addShownPointIds(ringPointIds))}
      onCollapse={() => dispatch(removeShownPointIds(ringPointIds))}
      header={<Typography>{header}</Typography>}
      action={
        canEditFarm && ring && !boundaryEdit && !isEditingThisRing
          ? { icon: <EditIcon fontSize="small" />, onClick: startEdit }
          : undefined
      }
    >
      {isEditingThisRing ? <EditBoundary /> : <PointList points={ringPoints} />}
    </Section>
  );
};

export const BoundaryRingSections: FC<{ zone: ZoneType }> = ({ zone }) => {
  const { t } = useTranslation();
  const boundaryAreaIndexes = useMemo(
    () =>
      zone.areas.flatMap((area, index) =>
        area.polygon?.boundary ? [index] : []
      ),
    [zone]
  );
  const holeRings = useMemo(
    () =>
      zone.areas.flatMap((area, areaIndex) =>
        (area.polygon?.holes ?? []).map((_hole, holeIndex) => ({
          areaIndex,
          holeIndex,
        }))
      ),
    [zone]
  );
  return (
    <>
      {boundaryAreaIndexes.map((areaIndex) => (
        <BoundaryRingSection
          key={areaIndex}
          zone={zone}
          areaIndex={areaIndex}
          header={
            boundaryAreaIndexes.length > 1
              ? t("views.farms.editBoundary.boundaryWithArea", {
                  number: areaIndex + 1,
                })
              : t("views.farms.detailsPanel.boundary")
          }
        />
      ))}
      {holeRings.map(({ areaIndex, holeIndex }, i) => (
        <BoundaryRingSection
          key={`${areaIndex}-${holeIndex}`}
          zone={zone}
          areaIndex={areaIndex}
          holeIndex={holeIndex}
          header={t("views.farms.editBoundary.hole", { number: i + 1 })}
        />
      ))}
    </>
  );
};

function displayPoint(pt: { lat: number; lng: number }): string {
  const LAT_LON_DECIMALS = 6; // ~10 cm
  return `(${roundToNPlaces(pt.lng, LAT_LON_DECIMALS)}, ${roundToNPlaces(
    pt.lat,
    LAT_LON_DECIMALS
  )})`;
}

interface SortablePoint {
  id: string;
  index: number;
  point: DraftBoundaryPoint;
}

interface PointRowProps {
  point: DraftBoundaryPoint;
  active: boolean;
  onSelect: (pointId: string) => void;
  onDelete: (pointId: string) => void;
}

// memoized: dnd-kit re-renders every row on every pointer move of a drag
const PointRow = memo(function PointRow({
  point,
  active,
  onSelect,
  onDelete,
}: PointRowProps) {
  const primaryColor = useMuiColor("primary");
  return (
    <Card
      className="my-1"
      style={active ? { backgroundColor: alpha(primaryColor, 0.3) } : undefined}
    >
      <CardActionArea
        component="div"
        className="flex flex-row items-center cursor-grab"
        onClick={() => onSelect(point.pointId)}
      >
        <DragIndicatorIcon className="ml-2 mr-1" fontSize="small" />
        <span className="grow text-sm">
          <span className="font-bold mr-1">{point.ordinal}</span>
          {displayPoint(point)}
        </span>
        <IconButton
          edge="end"
          size="small"
          color="error"
          className="mx-2"
          aria-label="delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(point.pointId);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActionArea>
    </Card>
  );
});

const PROBLEM_I18N_KEYS: Record<
  NewZoneKind | "ring",
  Partial<Record<BoundaryEditProblem, string>>
> = {
  ring: {
    notEnoughPoints: "views.farms.editBoundary.notEnoughPoints",
    selfIntersecting: "views.farms.editBoundary.isSelfIntersecting",
  },
  farmBoundary: {
    notEnoughPoints: "views.farms.setFarmBoundary.notEnoughPoints",
    selfIntersecting: "views.farms.setFarmBoundary.isSelfIntersecting",
  },
  obstacle: {
    notEnoughPoints: "views.farms.newObstacle.notEnoughPoints",
    selfIntersecting: "views.farms.newObstacle.isSelfIntersecting",
    nameRequired: "views.farms.newObstacle.nameRequired",
  },
  centerPivot: {
    notEnoughPoints: "views.farms.newCenterPivot.needCenter",
    nameRequired: "views.farms.newCenterPivot.nameRequired",
  },
};

export const EditBoundary: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { boundaryEdit } = useFarmExplorer();

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const points = useMemo(
    () => boundaryEdit?.points ?? [],
    [boundaryEdit?.points]
  );
  const activePointId = boundaryEdit?.activePointId;
  const pending = boundaryEdit?.pending;

  const problem = useMemo(
    () => boundaryEditProblem({ points, pending }),
    [points, pending]
  );
  const problemI18nKey =
    problem && PROBLEM_I18N_KEYS[pending?.kind ?? "ring"][problem];

  const sortablePoints: SortablePoint[] = useMemo(
    () => points.map((pt, idx) => ({ id: pt.pointId, index: idx, point: pt })),
    [points]
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = points.findIndex(({ pointId }) => pointId === active.id);
    const newIndex = points.findIndex(({ pointId }) => pointId === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      console.error(
        `EditBoundary: couldn't find dragged ids ${active.id}/${over.id}`
      );
      return;
    }
    const next = [...points];
    moveItem(next, oldIndex, newIndex);
    dispatch(setBoundaryEditPoints(next));
  };

  const selectPoint = useCallback(
    (pointId: string) => dispatch(setBoundaryEditActivePoint(pointId)),
    [dispatch]
  );
  const deletePoint = useCallback(
    (pointId: string) =>
      dispatch(
        setBoundaryEditPoints(points.filter((pt) => pt.pointId !== pointId))
      ),
    [dispatch, points]
  );
  const renderPoint = ({ point }: SortablePoint): JSX.Element => (
    <PointRow
      point={point}
      active={point.pointId === activePointId}
      onSelect={selectPoint}
      onDelete={deletePoint}
    />
  );

  return (
    <div className="flex flex-col">
      <div className="max-h-96 overflow-y-auto">
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContainer
            id="boundary-edit-points"
            items={sortablePoints}
            render={renderPoint}
          />
        </DndContext>
      </div>
      {problemI18nKey && (
        <Typography className="p-2 text-sm" color="warning">
          {t(problemI18nKey)}
        </Typography>
      )}
    </div>
  );
};
